import React, { useState, useRef } from 'react';
import styled, { css, DefaultTheme, useTheme } from 'styled-components';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnPinningState,
  SortingState,
  ColumnDef,
  RowData,
  Table as ReactTableType,
  ColumnMeta,
  RowSelectionState,
  OnChangeFn,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { prop } from 'styled-tools';
import { CSSObject } from 'styled-components';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    isCentered?: boolean;
    styles?: CSSObject;
  }
}

export const TableHeader = styled.thead<{
  shouldPinHeaderRow?: boolean;
}>`
  border-bottom: 1px solid ${prop('theme.colors.bgActive')};
  ${({ shouldPinHeaderRow }) =>
    shouldPinHeaderRow &&
    css`
      position: sticky;
      top: 0;
      z-index: 3;
      background: inherit;

      tr {
        background-color: #161617 !important;
      }
    `}
`;

export const TableBody = styled.tbody``;

export const TableRow = styled.tr<{
  isSelected?: boolean;
  isHovered?: boolean;
}>`
  transition: all 0.3s ease-in-out;
  background-color: ${({ isSelected, isHovered, theme }) => {
    if (isSelected) return theme.colors.inputBackground;
    if (isHovered) return theme.colors.bgDefaultSecondary;
    return 'transparent';
  }};
`;

export const TableHead = styled.th<{
  meta?: ColumnMeta<unknown, unknown>;
}>`
  font-weight: bold;
  text-align: ${({ meta }) => (meta?.isCentered ? 'center' : 'left')};

  &:not(:last-child) {
    border-right: 1px solid ${prop('theme.colors.bgActive')};
  }

  ${({ meta }) => meta?.styles && css(meta.styles)}
`;

export const TableCell = styled.td<{
  meta?: ColumnMeta<unknown, unknown>;
  isHovered?: boolean;
}>`
  transition: all 0.3s ease-in-out;
  color: ${prop('theme.colors.textActive')};
  ${({ meta }) => meta?.isCentered && `text-align: center;`}

  &:not(:last-child) {
    border-right: 1px solid ${prop('theme.colors.bgActive')};
  }

  ${({ isHovered }) =>
    isHovered &&
    css`
      cursor: pointer;
    `}
  ${({ meta }) => meta?.styles && css(meta.styles)}
`;

const StyledTable = styled.table`
  background-color: transparent;
  border: none;
  border-collapse: inherit;
  border-spacing: 0px;
  margin-top: 0;
  padding-bottom: 0;

  th {
    background-color: transparent !important;
    padding-top: 10px;
    padding-bottom: 10px;
    border-top: 1px solid ${({ theme }) => theme.colors.bgActive} !important;
    border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive} !important;
    color: ${({ theme }) => theme.colors.textRegular};
    font-weight: 500;
  }

  th[data-pinned='true'] {
    background-color: #161617 !important;
  }
  th[data-pinned='true'],
  td[data-pinned='true'] {
    border-right: 1px solid ${({ theme }) => theme.colors.divider} !important;
  }

  td {
    background-color: transparent;
    padding-top: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
    color: ${({ theme }) => theme.colors.textActive};
    vertical-align: middle;
  }

  thead > tr > th:first-child {
    border-top-left-radius: 8px;
    border-left: 1px solid ${prop('theme.colors.bgActive')};
  }

  thead > tr > th:last-child {
    border-top-right-radius: 8px;
    border-right: 1px solid ${prop('theme.colors.bgActive')};
  }

  thead > tr:last-child > th {
    border-top: 1px solid ${prop('theme.colors.bgActive')};
    border-bottom: 1px solid ${prop('theme.colors.bgActive')};
  }

  tbody > tr:last-child > td:first-child {
    border-bottom-left-radius: 8px;
    overflow: hidden;
  }

  tbody > tr:last-child > td:last-child {
    border-bottom-right-radius: 8px;
  }

  tbody > tr > td:first-child {
    border-left: 1px solid ${prop('theme.colors.bgActive')};
  }

  tbody > tr > td:last-child {
    border-right: 1px solid ${prop('theme.colors.bgActive')};
  }

  tbody > tr:last-child > td {
    border-bottom: 1px solid ${prop('theme.colors.bgActive')};
  }

  thead > tr > th:first-child,
  tbody > tr > td:first-child {
    padding-left: 16px;
  }

  thead > tr > th:last-child,
  tbody > tr > td:last-child {
    padding-right: 16px;
  }

  thead > tr {
    background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  }

  tfoot > tr > td {
    border-bottom: 0;
  }
`;

const TableScrollWrapper = styled.div<{ enableVirtualization?: boolean }>`
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  ${({ enableVirtualization }) =>
    enableVirtualization &&
    css`
      overflow-y: auto;
    `}
  padding-bottom: 8px;
  width: 100%;

  &::-webkit-scrollbar {
    height: 6px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.bgActive};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.bgActive} transparent;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

// --- Pinning Styles Helper ---
function getCommonPinningStyles<T extends RowData>(
  column: ReactTableType<T>['getAllColumns'] extends () => Array<infer C>
    ? C
    : never,
  theme: DefaultTheme,
  isRowHovered?: boolean
): React.CSSProperties {
  const isPinned = column.getIsPinned?.();

  const backgroundColor = isRowHovered
    ? '#222224'
    : isPinned
    ? theme.colors.bgPage
    : undefined;
  return {
    position: isPinned ? 'sticky' : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    zIndex: isPinned ? 2 : 1,
    background: backgroundColor,
  };
}

// --- AdvancedTable Props ---
export interface TableProps<T extends RowData> {
  data: T[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[];
  getRowId?: (row: T) => string;
  initialSorting?: SortingState;
  initialPinning?: ColumnPinningState;
  footer?: React.ReactNode;
  className?: string;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  getRowProps?: (
    row: ReturnType<ReactTableType<T>['getRowModel']>['rows'][number]
  ) => React.HTMLAttributes<HTMLTableRowElement>;
  shouldPinHeaderRow?: boolean; // <-- new prop
  onDeleteRow?: (rowIndex: number) => void;
  onUpdateData?: (rowIndex: number, columnId: string, value: string) => void;
  enableVirtualization?: boolean;
  virtualizationRowHeight?: number;
}

export function Table<T extends RowData>({
  data,
  columns,
  getRowId,
  initialSorting = [],
  initialPinning = { left: [], right: [] },
  footer,
  className,
  rowSelection,
  onRowSelectionChange,
  getRowProps,
  onDeleteRow,
  onUpdateData,
  shouldPinHeaderRow = false, // <-- default false
  enableVirtualization = false,
  virtualizationRowHeight = 50,
}: TableProps<T>) {
  const theme = useTheme();
  const [columnPinning, setColumnPinning] =
    useState<ColumnPinningState>(initialPinning);
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  // If rowSelection is provided, use controlled selection; otherwise, let TanStack manage it internally
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnPinning,
      sorting,
      ...(rowSelection !== undefined ? { rowSelection } : {}),
    },
    meta: {
      onDeleteRow,
      onUpdateData,
    },
    onColumnPinningChange: setColumnPinning,
    onSortingChange: setSorting,
    ...(onRowSelectionChange ? { onRowSelectionChange } : {}),
    enableColumnPinning: true,
    enableRowSelection: true,
    ...(getRowId ? { getRowId } : {}),
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: enableVirtualization ? rows.length : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => virtualizationRowHeight,
    overscan: 5,
  });

  const renderRow = (
    row: ReturnType<typeof table.getRowModel>['rows'][number],
    style?: React.CSSProperties
  ) => {
    const rowProps = getRowProps ? getRowProps(row) : {};
    const hasOnClick = typeof rowProps.onClick === 'function';
    const shouldHighlightRow = hoveredRowId === row.id && hasOnClick;
    return (
      <TableRow
        key={row.id}
        {...rowProps}
        isHovered={shouldHighlightRow}
        style={{
          ...(rowProps.style || {}),
          ...(style || {}),
          ...(hasOnClick
            ? {
                cursor: 'pointer',
                transition: 'background 0.2s ease-in-out',
              }
            : {}),
        }}
        onMouseEnter={
          hasOnClick
            ? (e) => {
                setHoveredRowId(row.id);
                if (rowProps.onMouseEnter) rowProps.onMouseEnter(e);
              }
            : rowProps.onMouseEnter
        }
        onMouseLeave={
          hasOnClick
            ? (e) => {
                setHoveredRowId(null);
                if (rowProps.onMouseLeave) rowProps.onMouseLeave(e);
              }
            : rowProps.onMouseLeave
        }
      >
        {row
          .getVisibleCells()
          .map(
            (cell: ReturnType<typeof row.getVisibleCells>[number]) => (
              <TableCell
                key={cell.id}
                meta={cell.column.columnDef.meta}
                isHovered={shouldHighlightRow}
                style={getCommonPinningStyles(
                  cell.column,
                  theme,
                  shouldHighlightRow
                )}
                data-pinned={
                  cell.column.getIsPinned() ? 'true' : 'false'
                }
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            )
          )}
      </TableRow>
    );
  };

  return (
    <Wrapper className={'table-outer-wrapper'}>
      <TableScrollWrapper
        ref={parentRef}
        className={'table-scroll-wrapper'}
        enableVirtualization={enableVirtualization}
      >
        <StyledTable className={className}>
          <TableHeader shouldPinHeaderRow={shouldPinHeaderRow}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    meta={header.column.columnDef.meta}
                    style={getCommonPinningStyles(header.column, theme)}
                    data-pinned={header.column.getIsPinned() ? 'true' : 'false'}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        style={{
                          cursor: header.column.getCanSort()
                            ? 'pointer'
                            : undefined,
                          userSelect: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          justifyContent:
                            header.column.columnDef.meta?.styles?.textAlign ===
                            'right'
                              ? 'flex-end'
                              : header.column.columnDef.meta?.styles
                                  ?.textAlign === 'center'
                              ? 'center'
                              : 'flex-start',
                          textAlign:
                            header.column.columnDef.meta?.styles?.textAlign ||
                            undefined,
                          width: '100%',
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                        title={
                          header.column.getCanSort()
                            ? header.column.getNextSortingOrder() === 'asc'
                              ? 'Sort ascending'
                              : header.column.getNextSortingOrder() === 'desc'
                              ? 'Sort descending'
                              : 'Clear sort'
                            : undefined
                        }
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {/* Sorting icon logic */}
                        {header.column.getCanSort() && (
                          <i
                            className={
                              header.column.getIsSorted() === 'asc'
                                ? 'fa-solid fa-arrow-up'
                                : header.column.getIsSorted() === 'desc'
                                ? 'fa-solid fa-arrow-down'
                                : 'fa-solid fa-arrows-up-down'
                            }
                            style={{
                              marginLeft: 4,
                              fontSize: 14,
                              opacity: 0.8,
                            }}
                            aria-label={
                              header.column.getIsSorted() === 'asc'
                                ? 'Sorted ascending'
                                : header.column.getIsSorted() === 'desc'
                                ? 'Sorted descending'
                                : 'Not sorted'
                            }
                          />
                        )}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {enableVirtualization
              ? (() => {
                  const virtualItems = rowVirtualizer.getVirtualItems();
                  const totalSize = rowVirtualizer.getTotalSize();
                  const paddingTop =
                    virtualItems.length > 0 ? virtualItems[0]?.start ?? 0 : 0;
                  const paddingBottom =
                    virtualItems.length > 0
                      ? totalSize -
                        (virtualItems[virtualItems.length - 1]?.end ?? 0)
                      : 0;

                  return (
                    <>
                      {paddingTop > 0 && (
                        <tr>
                          <td
                            style={{
                              height: `${paddingTop}px`,
                              padding: 0,
                              border: 'none',
                            }}
                            colSpan={columns.length}
                          />
                        </tr>
                      )}
                      {virtualItems.map((virtualRow) => {
                        const row = rows[virtualRow.index];
                        return (
                          <React.Fragment key={row.id}>
                            {renderRow(row)}
                          </React.Fragment>
                        );
                      })}
                      {paddingBottom > 0 && (
                        <tr>
                          <td
                            style={{
                              height: `${paddingBottom}px`,
                              padding: 0,
                              border: 'none',
                            }}
                            colSpan={columns.length}
                          />
                        </tr>
                      )}
                    </>
                  );
                })()
              : rows.map((row) => renderRow(row))}
          </TableBody>
        </StyledTable>
      </TableScrollWrapper>
      {footer && <div style={{ width: '100%' }}>{footer}</div>}
    </Wrapper>
  );
}

export default Table;
